import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router } from "react-router-dom";
import Amplify, { API, Auth } from "aws-amplify";
import * as Sentry from "@sentry/react";
import { BrowserTracing, Replay } from "@sentry/browser";

import App from "./App";
import i18n from "./i18n";
import { HelmetProvider } from "react-helmet-async";
import registerSW, { unregister as unregisterSW } from "./registerServiceWorker";
import config from "./config";
import TagManager from "react-gtm-module";
import { getSavedLang, saveLang, setLangInUrl } from "./utils/lang";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { blue } from "@material-ui/core/colors";
import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import { getEnvironment } from "./utils/environment";
import { clearMarketplaceSessionTokenCache, getMarketplaceSessionToken } from "./utils/marketplaceSession";
import { enforceDomainSecurity } from "./utils/domainGuard";
import { captureAnalyticsDebugFlag, gtmContainerId } from "./analytics/context";
import analytics from "./analytics";

const isAuthorizedDomain = enforceDomainSecurity({
  onViolation: (host) => {
    if (typeof Sentry !== "undefined" && Sentry.captureMessage) {
      Sentry.captureMessage(`Unauthorized domain access blocked: ${host}`, "warning");
    }
  },
});

const __host = (typeof window !== "undefined" && window.location && window.location.hostname) ? window.location.hostname.toLowerCase() : "";
const __envFromHost = getEnvironment();
const __isLocal = __host.includes("local") || __host.includes("127.0.0.1");

const debugEnabled =
  __host === "development.fansfirst.ca" &&
  (() => {
    try {
      return localStorage.getItem("ff_analytics_debug") === "1";
    } catch {
      return false;
    }
  })();

captureAnalyticsDebugFlag(debugEnabled);
analytics.init();

window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
  analytics_debug_enabled: debugEnabled === true,
});

const tagManagerArgs = {
  gtmId: gtmContainerId(window.location.hostname),
};

TagManager.initialize(tagManagerArgs);
if (!__isLocal) {
  Sentry.init({
    dsn: "https://5813a200ee1313cd4d569a12bcea98e0@o4511725856686080.ingest.us.sentry.io/4511725870252032",
    environment: __envFromHost,
    release: "1.6.1",
    // debug only on development domain
    debug: true,
    ignoreErrors: [
      // Silent failures in minor UI components or third-party scripts
      "Invalid attempt to spread non-iterable instance",
      
      // Harmless browser warning that doesn't break the page
      "ResizeObserver loop limit exceeded",
      
      // Thrown by messy third-party analytics scripts or browser extensions
      "Non-Error exception captured",
      "Non-Error promise rejection captured",
      
      // Generic network drops (ad-blocker, dropped wi-fi, etc.)
      "Network Error",
      "Failed to fetch",
      "Load failed",
      
      // Chrome Extension background crashes (e.g., password managers)
      "Invalid call to runtime.sendMessage(). Tab not found.",
      
      // Firefox abruptly killing a network request when a tab is closed
      "NS_ERROR_FAILURE"
    ],
    dataCollection: {
      // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
      // userInfo: false,
      // httpBodies: []
    },
    integrations: [
      new BrowserTracing(),
      new Replay()
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [process.env.REACT_APP_NEW_API_BASE_URL || /^https:\/\/api\.fansfirst\.ca/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  });
}


const breakpoints = createBreakpoints({});

const pxToRem = (value) => {
  return `${value / 16}rem`;
};

const theme = createMuiTheme({
  palette: {
    primary: blue,
    secondary: {
      main: "#e03a3e",
    },
    tertiary: {
      main: "#e03a3e",
    },
  },
  status: {
    danger: "orange",
  },
  breakpoints,
  overrides: {
    MuiTypography: {
      headline: {
        fontSize: pxToRem(24),
        marginBottom: ".25rem",
        [breakpoints.up("md")]: {
          fontSize: pxToRem(32),
        },
      },
      title: {
        fontSize: pxToRem(21),
        [breakpoints.up("md")]: {
          fontSize: pxToRem(24),
        },
      },
      body1: {
        fontSize: pxToRem(14),
        lineHeight: 1.5,
        [breakpoints.up("md")]: {
          fontSize: pxToRem(16),
        },
      },
      body2: {
        fontSize: pxToRem(14),
        lineHeight: 1.5,
        [breakpoints.up("md")]: {
          fontSize: pxToRem(16),
        },
      },
      button: {
        fontSize: pxToRem(14),
        [breakpoints.up("md")]: {
          fontSize: pxToRem(16),
        },
      },
      caption: {
        fontSize: pxToRem(12),
        [breakpoints.up("md")]: {
          fontSize: pxToRem(14),
        },
      },
    },
  },
  shadows: [
    "none",
    "0px 2px 3px 0px rgba(0, 0, 0, 0.07),0px 4px 5px 0px rgba(0, 0, 0, 0.03),0px 2px 1px -1px rgba(0, 0, 0, 0.02)",
    "0px 2px 5px 0px rgba(0, 0, 0, 0.07),0px 2px 2px 0px rgba(0, 0, 0, 0.03),0px 3px 1px -2px rgba(0, 0, 0, 0.02)",
    "0px 2px 9px 0px rgba(0, 0, 0, 0.07),0px 1px 3px 0px rgba(0, 0, 0, 0.03),0px 3px 3px -2px rgba(0, 0, 0, 0.02)",
    "0px 4px 4px -1px rgba(0, 0, 0, 0.07),0px 0px 5px 0px rgba(0, 0, 0, 0.03),0px 1px 10px 0px rgba(0, 0, 0, 0.02)",
    "0px 6px 6px -1px rgba(0, 0, 0, 0.07),0px -1px 10px 0px rgba(0, 0, 0, 0.03),0px 1px 14px 0px rgba(0, 0, 0, 0.02)",
    "0px 6px 6px -1px rgba(0, 0, 0, 0.07),0px -2px 12px 0px rgba(0, 0, 0, 0.03),0px 1px 18px 0px rgba(0, 0, 0, 0.02)",
    "0px 7px 6px -2px rgba(0, 0, 0, 0.07),0px -1px 12px 1px rgba(0, 0, 0, 0.03),0px 2px 16px 1px rgba(0, 0, 0, 0.02)",
    "0px 10px 6px -3px rgba(0, 0, 0, 0.07),0px 0px 12px 1px rgba(0, 0, 0, 0.03),0px 3px 14px 2px rgba(0, 0, 0, 0.02)",
    "0px 10px 7px -3px rgba(0, 0, 0, 0.07),0px 1px 14px 1px rgba(0, 0, 0, 0.03),0px 3px 16px 2px rgba(0, 0, 0, 0.02)",
    "0px 11px 7px -3px rgba(0, 0, 0, 0.07),0px 2px 16px 1px rgba(0, 0, 0, 0.03),0px 4px 18px 3px rgba(0, 0, 0, 0.02)",
    "0px 11px 8px -4px rgba(0, 0, 0, 0.07),0px 3px 17px 1px rgba(0, 0, 0, 0.03),0px 4px 20px 3px rgba(0, 0, 0, 0.02)",
    "0px 13px 9px -4px rgba(0, 0, 0, 0.07),0px 4px 19px 2px rgba(0, 0, 0, 0.03),0px 5px 22px 4px rgba(0, 0, 0, 0.02)",
    "0px 13px 9px -4px rgba(0, 0, 0, 0.07),0px 5px 21px 2px rgba(0, 0, 0, 0.03),0px 5px 24px 4px rgba(0, 0, 0, 0.02)",
    "0px 13px 10px -4px rgba(0, 0, 0, 0.07),0px 6px 23px 2px rgba(0, 0, 0, 0.03),0px 5px 26px 4px rgba(0, 0, 0, 0.02)",
    "0px 15px 10px -5px rgba(0, 0, 0, 0.07),0px 7px 24px 2px rgba(0, 0, 0, 0.03),0px 6px 28px 5px rgba(0, 0, 0, 0.02)",
    "0px 15px 12px -5px rgba(0, 0, 0, 0.07),0px 8px 26px 2px rgba(0, 0, 0, 0.03),0px 6px 30px 5px rgba(0, 0, 0, 0.02)",
    "0px 15px 13px -5px rgba(0, 0, 0, 0.07),0px 9px 28px 2px rgba(0, 0, 0, 0.03),0px 6px 32px 5px rgba(0, 0, 0, 0.02)",
    "0px 17px 13px -5px rgba(0, 0, 0, 0.07),0px 10px 30px 2px rgba(0, 0, 0, 0.03),0px 7px 34px 6px rgba(0, 0, 0, 0.02)",
    "0px 17px 14px -6px rgba(0, 0, 0, 0.07),0px 11px 31px 2px rgba(0, 0, 0, 0.03),0px 7px 36px 6px rgba(0, 0, 0, 0.02)",
    "0px 19px 15px -6px rgba(0, 0, 0, 0.07),0px 12px 33px 3px rgba(0, 0, 0, 0.03),0px 8px 38px 7px rgba(0, 0, 0, 0.02)",
    "0px 19px 15px -6px rgba(0, 0, 0, 0.07),0px 13px 35px 3px rgba(0, 0, 0, 0.03),0px 8px 40px 7px rgba(0, 0, 0, 0.02)",
    "0px 19px 16px -6px rgba(0, 0, 0, 0.07),0px 14px 37px 3px rgba(0, 0, 0, 0.03),0px 8px 42px 7px rgba(0, 0, 0, 0.02)",
    "0px 20px 16px -7px rgba(0, 0, 0, 0.07),0px 15px 38px 3px rgba(0, 0, 0, 0.03),0px 9px 44px 8px rgba(0, 0, 0, 0.02)",
    "0px 20px 18px -7px rgba(0, 0, 0, 0.07),0px 16px 40px 3px rgba(0, 0, 0, 0.03),0px 9px 46px 8px rgba(0, 0, 0, 0.02)",
  ],
});

const setQcCookie = async () => {
  try {
    const savedLang = getSavedLang();
    if (savedLang) {
      return;
    }

    const getCookie = (name) => {
      const found = document.cookie
        .split("; ")
        .find((row) => row.startsWith(name + "="));
      if (!found) return undefined;
      const parts = found.split("=");
      return parts.length > 1 ? parts[1] : undefined;
    };

    const setCookie = (name, value, maxAgeSeconds = 2592000) => {
      const parts = window.location.hostname.split(".");
      const rootDomain = parts.length >= 2 ? parts.slice(-2).join(".") : window.location.hostname;
      const cookie = [
        `${name}=${value}`,
        "Path=/",
        `Max-Age=${maxAgeSeconds}`,
        "SameSite=Lax",
        "Secure",
        `Domain=.${rootDomain}`,
      ].join("; ");
      document.cookie = cookie;
    };

    let isQc = null;
    let currentIp = null;
    const lastIp = getCookie("ff_last_ip") || null;

    try {
      const dj = await API.get("v2", "utils/ip/details");
      if (dj && (dj.success === true || typeof dj.is_qc !== "undefined")) {
        isQc = !!dj.is_qc;
        currentIp = dj.ip || dj.ip_address || null;
      }
    } catch (_) {
      // abstract fail
    }

    if (isQc === null) return;

    if (currentIp && lastIp && currentIp !== lastIp) {
      try {
        const dj2 = await API.get("v2", `utils/ip/details?ip=${encodeURIComponent(currentIp)}`);
        if (dj2 && (dj2.success === true || typeof dj2.is_qc !== "undefined")) {
          isQc = !!dj2.is_qc;
          currentIp = dj2.ip || dj2.ip_address || currentIp;
        }
      } catch (_) {
        // abstract fail
      }
    }

    setCookie("ff_is_qc", isQc ? "1" : "0");
    if (currentIp) setCookie("ff_last_ip", currentIp);

    try {
      const urlLang = (() => {
        try {
          const params = new URLSearchParams(window.location.search);
          const l = params.get("lang");
          return l === "fr" || l === "en" ? l : null;
        } catch {
          return null;
        }
      })();
      const target = isQc ? "fr" : "en";
      if (!urlLang && !savedLang) {
        if (i18n && i18n.language !== target) {
          i18n.changeLanguage(target);
        }
        saveLang(target);
        try {
          const params = new URLSearchParams(window.location.search);
          const current = params.get("lang");
          if (current !== target) setLangInUrl(target);
        } catch {}
      }
    } catch (_) {
      // abstract fail
    }
  } catch (e) {
    // abstract fail
  }
};

const isMarketplaceForbiddenError = (error) => {
  const response = error && error.response ? error.response : {};
  const statusCandidates = [
    response.status,
    response.statusCode,
    error && error.status,
    error && error.statusCode,
  ];

  return statusCandidates.some((status) => Number(status) === 403);
};

const shouldRecoverMarketplaceSession = (apiName, path) => {
  if (apiName !== "v2" || typeof path !== "string") {
    return false;
  }

  return path.startsWith("marketplace/") || path.startsWith("communication/");
};

const installMarketplaceSessionRecovery = () => {
  if (API.__ffMarketplaceSessionRecoveryInstalled) {
    return;
  }

  ["get", "post", "put", "del"].forEach((method) => {
    const original = API[method].bind(API);
    API[method] = async (apiName, path, init = {}) => {
      if (!shouldRecoverMarketplaceSession(apiName, path)) {
        return original(apiName, path, init);
      }

      try {
        return await original(apiName, path, init);
      } catch (error) {
        if (!isMarketplaceForbiddenError(error)) {
          throw error;
        }

        clearMarketplaceSessionTokenCache();
        await getMarketplaceSessionToken(process.env.REACT_APP_NEW_API_BASE_URL);
        return original(apiName, path, init);
      }
    };
  });

  API.__ffMarketplaceSessionRecoveryInstalled = true;
};

Amplify.configure({
  Auth: {
    mandatorySignIn: false,
    region: config.cognito.REGION,
    userPoolId: config.cognito.USER_POOL_ID,
    identityPoolId: config.cognito.IDENTITY_POOL_ID,
    userPoolWebClientId: config.cognito.APP_CLIENT_ID,
  },
  API: {
    endpoints: [
      {
        name: "v2",
        endpoint: process.env.REACT_APP_NEW_API_BASE_URL,
        region: config.apiGateway.REGION,
        custom_header: async () => {
          const headers = {};
          try {
            const session = await Auth.currentSession();
            headers["Authorization"] = session.getIdToken().getJwtToken();
          } catch (e) {
            // Not logged in
          }

          // Inject Marketplace Session
          const mktToken = await getMarketplaceSessionToken(process.env.REACT_APP_NEW_API_BASE_URL);
          if (mktToken) {
            headers["X-Marketplace-Session"] = mktToken;
          }

          return headers;
        },
      },
    ],
  },
});

installMarketplaceSessionRecovery();

const primeMarketplaceSessionToken = async () => {
  try {
    await getMarketplaceSessionToken(process.env.REACT_APP_NEW_API_BASE_URL);
  } catch (_) {
    // Ignore best-effort refresh failures and let request-time flow recover.
  }
};

window.addEventListener("focus", primeMarketplaceSessionToken);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    primeMarketplaceSessionToken();
  }
});

primeMarketplaceSessionToken();

setQcCookie();

if (isAuthorizedDomain) {
  ReactDOM.render(
    <Router>
      <MuiThemeProvider theme={theme}>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </MuiThemeProvider>
    </Router>,
    document.getElementById("root")
  );
}

// Explicitly unregister any previously registered service worker so
// this branch no longer runs as a PWA.
unregisterSW();
